import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, Avatar, Chip, CircularProgress, Alert, Tabs, Tab,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowBack, Info as InfoIcon, Shop2 } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { fetchProductsByProfilAndAge, clearKidProfileProducts } from '../store/productsSlice';
import { fetchKids } from '../store/slices/kidSlice';
import { fetchKidNeuroStatus, fetchNeuroResult, clearNeuroState } from '../store/slices/neuroSlice';
import NeuroResultsPanel from '../components/neuro/NeuroResultsPanel';
import ListVideo from '../components/video/ListVideo';

const parsePaths = (kid) => {
  if (!kid?.profiling_paths_completed) return { pedagogical: false, neuro: false };
  if (typeof kid.profiling_paths_completed === 'string') {
    try { return JSON.parse(kid.profiling_paths_completed); } catch { return { pedagogical: false, neuro: false }; }
  }
  return kid.profiling_paths_completed;
};

const TabPanel = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const KidProfilePage = () => {
  const { kidId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { kids, loading, error } = useSelector((state) => state.kid);
  const { user } = useSelector((state) => state.auth);
  const { kidProfileProducts: recommendedProducts, loading: productsLoading, error: productsError } = useSelector((state) => state.products);
  const { kidStatus, result: neuroResult, resultKidId, kidStatusKidId } = useSelector((state) => state.neuro);

  const [kid, setKid] = useState(null);
  const [neuroLoading, setNeuroLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(location.state?.neuroTab ? 1 : 0);
  const [parsedProfileType, setParsedProfileType] = useState(null);

  useEffect(() => {
    if (!user) {
      toast.info(
        <div>
          <p><strong>Vous devez vous connecter</strong></p>
          <p>Connectez-vous pour voir ce profil</p>
        </div>,
        { position: 'top-center', autoClose: 5000, theme: 'light' }
      );
      setTimeout(() => navigate('/auth'), 2000);
      return;
    }
    if (kids.length === 0) {
      dispatch(fetchKids());
    }
  }, [user, kids.length, navigate, dispatch]);

  useEffect(() => {
    if (!user || kids.length === 0) return;

    const foundKid = kids.find((k) => k.id === Number(kidId) || k.id === kidId);
    if (!foundKid) {
      toast.error("Profil d'enfant non trouvé", { theme: 'light' });
      return;
    }

    dispatch(clearNeuroState());
    setKid(foundKid);

    if (foundKid.profil_type_id && foundKid.ages_section_id) {
      dispatch(clearKidProfileProducts());
      dispatch(fetchProductsByProfilAndAge({
        profilTypeId: foundKid.profil_type_id,
        agesSectionId: foundKid.ages_section_id,
      }));
    } else {
      dispatch(clearKidProfileProducts());
    }

    setNeuroLoading(true);
    dispatch(fetchKidNeuroStatus(foundKid.id))
      .unwrap()
      .then((status) => {
        const evalId = status?.lastCompleted?.id || foundKid.last_neuro_evaluation_id;
        if (evalId) {
          dispatch(fetchNeuroResult({ evaluationId: evalId, kidId: foundKid.id }));
        }
      })
      .finally(() => setNeuroLoading(false));
  }, [kidId, kids, user, dispatch]);

  useEffect(() => {
    if (!kid) return;
    if (kid.profil_type_id && !kid.profil_type) {
      setParsedProfileType({
        id: kid.profil_type_id,
        name: `Profil #${kid.profil_type_id}`,
      });
    }
  }, [kid]);

  const handleGoBack = () => navigate('/HomePage');

  const isPedagogicalDone = !!(kid?.profil_type_id || kid?.profil_type);
  const paths = kid ? parsePaths(kid) : {};
  const currentKidId = kid ? Number(kid.id) : null;
  const statusForKid = kidStatusKidId === currentKidId ? kidStatus : null;
  const isNeuroDone = !!(
    kid?.neuro_summary ||
    kid?.last_neuro_evaluation_id ||
    paths.neuro ||
    statusForKid?.lastCompleted
  );
  const hasNeuroDraft = !!statusForKid?.draft;
  const neuroEvalId = kid?.last_neuro_evaluation_id || statusForKid?.lastCompleted?.id;
  const neuroResultForKid = (
    neuroResult?.scores &&
    neuroEvalId &&
    resultKidId === currentKidId
  ) ? neuroResult : null;

  const getNeuroChipLabel = () => {
    if (neuroLoading) return '…';
    if (isNeuroDone) return 'Terminé';
    if (hasNeuroDraft) return 'En cours';
    return 'À faire';
  };

  const getNeuroChipColor = () => {
    if (neuroLoading) return 'default';
    if (isNeuroDone) return 'success';
    if (hasNeuroDraft) return 'info';
    return 'warning';
  };

  const handleStartPedagogical = () => navigate(`/Profilling/${kidId}?exam=pedagogical`);
  const handleStartNeuro = () => navigate(`/Profilling/${kidId}?exam=neuro`);

  const getInitials = (firstname) => (firstname ? firstname.charAt(0).toUpperCase() : '?');

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#FFF7E5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={60} />
        <Typography sx={{ mt: 2 }}>Chargement du profil...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#FFF7E5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 500 }}>{error}</Alert>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={handleGoBack} sx={{ mt: 3, backgroundColor: '#FFD88C', color: '#000' }}>Retour</Button>
      </Box>
    );
  }

  if (!kid) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#FFF7E5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>Profil non trouvé</Typography>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={handleGoBack} sx={{ backgroundColor: '#FFD88C', color: '#000' }}>Retour</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', bgcolor: '#FFF7E5', p: { xs: 2, sm: 3 }, pt: { xs: 3, sm: 4 }, pb: { xs: 4, sm: 8 }, overflow: 'auto' }}>
      <Box sx={{ width: '100%', maxWidth: 720, mb: 2 }}>
        <Button variant="contained" startIcon={<ArrowBack />} onClick={handleGoBack}
          sx={{ backgroundColor: '#FFD88C', color: '#000', fontWeight: 'bold', '&:hover': { backgroundColor: '#FFC870' } }}>
          Retour
        </Button>
      </Box>

      <Paper elevation={3} sx={{ maxWidth: 720, width: '100%', borderRadius: { xs: 2, sm: 4 }, overflow: 'hidden', backgroundColor: 'white', mb: 4 }}>
        <Box sx={{ bgcolor: '#784e3c', py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Avatar sx={{ width: { xs: 80, sm: 120 }, height: { xs: 80, sm: 120 }, bgcolor: 'white', color: '#784e3c', fontSize: { xs: '2.5rem', sm: '3.5rem' }, fontWeight: 'bold', border: { xs: '3px solid white', sm: '4px solid white' } }}>
            {getInitials(kid.firstname)}
          </Avatar>
          <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mt: 2, textAlign: 'center', fontFamily: 'Achiko', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            {kid.firstname} {kid.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 1 }}>
            {kid.age_section?.name || 'Tranche d\'âge non spécifiée'}
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 1, sm: 2 } }}>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': { fontFamily: 'Achiko', fontWeight: 'bold', minHeight: 56 },
              '& .Mui-selected': { color: '#784e3c' },
              '& .MuiTabs-indicator': { backgroundColor: '#784e3c' },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Profil pédagogique
                  <Chip label={isPedagogicalDone ? 'Terminé' : 'À faire'} size="small"
                    color={isPedagogicalDone ? 'success' : 'warning'} sx={{ height: 20, fontSize: '0.65rem' }} />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Profil neuroatypique
                  <Chip label={getNeuroChipLabel()} size="small"
                    color={getNeuroChipColor()} sx={{ height: 20, fontSize: '0.65rem' }} />
                </Box>
              }
            />
          </Tabs>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          {/* Onglet 0 — Profil pédagogique */}
          <TabPanel value={tabIndex} index={0}>
            {isPedagogicalDone ? (
              <>
                <Chip
                  label={kid.profil_type?.name || parsedProfileType?.name || `Profil #${kid.profil_type_id}`}
                  sx={{ bgcolor: 'rgba(67, 172, 217, 0.1)', color: '#784e3c', fontWeight: 'bold', mb: 3 }}
                />
                <Box sx={{ bgcolor: 'rgba(67, 172, 217, 0.05)', border: '1px solid rgba(67, 172, 217, 0.2)', borderRadius: 2, p: 3, mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <InfoIcon sx={{ color: '#784e3c', mr: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#784e3c', fontSize: '1rem' }}>
                      Comment ce profil a été déterminé ?
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                    Notre analyse examine les réponses au questionnaire pédagogique pour identifier le style d&apos;apprentissage
                    (visuel, auditif, kinesthésique) et proposer des activités adaptées.
                  </Typography>
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 'bold', fontFamily: 'Achiko', color: '#784e3c', mb: 2 }}>
                  Activités recommandées
                </Typography>
                {productsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={40} /></Box>
                ) : productsError ? (
                  <Alert severity="error">{productsError}</Alert>
                ) : recommendedProducts?.length > 0 ? (
                  <ListVideo videos={recommendedProducts} />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Des activités adaptées sont en cours de préparation pour ce profil.
                  </Typography>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Button variant="contained" startIcon={<Shop2 />} onClick={() => navigate('/articles')}
                    sx={{ backgroundColor: '#FFD88C', color: '#000', fontWeight: 'bold', borderRadius: '25px', fontFamily: 'Achiko' }}>
                    Constituer sa Valise
                  </Button>
                </Box>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Le questionnaire pédagogique n&apos;a pas encore été complété pour {kid.firstname}.
                </Typography>
                <Button variant="contained" onClick={handleStartPedagogical}
                  sx={{ minHeight: 48, backgroundColor: '#784e3c', touchAction: 'manipulation' }}>
                  Compléter le questionnaire pédagogique
                </Button>
              </Box>
            )}
          </TabPanel>

          {/* Onglet 1 — Profil neuroatypique */}
          <TabPanel value={tabIndex} index={1}>
            {neuroLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#784e3c' }} />
              </Box>
            ) : isNeuroDone && neuroResultForKid ? (
              <NeuroResultsPanel
                result={neuroResultForKid}
                evaluationId={neuroEvalId}
                kid={kid}
                showTitle={false}
                showEmailSend={false}
                showRetake={false}
              />
            ) : isNeuroDone && !neuroResultForKid ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#784e3c' }} />
              </Box>
            ) : hasNeuroDraft ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  Un questionnaire est en cours — {statusForKid.draft.current_question_position || 1}/15 questions répondues.
                </Typography>
                <Button variant="contained" onClick={handleStartNeuro}
                  sx={{ minHeight: 48, backgroundColor: '#784e3c' }}>
                  Reprendre le questionnaire
                </Button>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                  Questionnaire optionnel de 15 questions pour une orientation bienveillante
                  (Dynamisme, Sensoriel, Apprentissage). Ce n&apos;est pas un diagnostic médical.
                </Typography>
                <Button variant="contained" onClick={handleStartNeuro}
                  sx={{ minHeight: 48, backgroundColor: '#784e3c', touchAction: 'manipulation' }}>
                  Commencer le questionnaire
                </Button>
              </Box>
            )}
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default KidProfilePage;
